/**
 * Time-animated weather layers (Wind, Temperature, Radar, ...): the frame list a layer's
 * index.json gives, the frame URLs built from it, and the one clock every active time
 * layer runs on. Pure logic, no map and no React, so it can be unit tested.
 *
 * Ported from the iOS time bar (TimeLayerIndex.swift / TimeLayerTimeline.swift on
 * feature/time-layers) so the three platforms agree on stops, "now", the play loop and
 * the ?r= cache key. Change them together.
 *
 * index.json lives next to the tiles at
 * https://pmtiles.basemap.com/tiles/<ServerLayerName>/index.json (tilelookup-pmtiles):
 *   { layer, step: "PT1H", default: "2026-10-19T17Z", times: [...],
 *     frames: { "<time>": { kind: observed|forecast, run, model, uv } }, updated }
 * `frames` is optional (snow depth and drought leave it out) and observed frames carry no
 * run. A layer can be forecast-only, so nothing may assume an observed frame exists.
 */

const HOUR = 3600
const DAY = 86400
// Play loops the layer's whole track, thinned only above this, which in practice is a
// 1-year daily range (Matt, matched with iOS and Android).
export const MAX_LOOP_FRAMES = 128

// ---- Formats ------------------------------------------------------------------------

/**
 * Frame keys come in three shapes: "2026-10-18T2130Z" (radar), "2026-10-18T20Z" (hourly
 * and six-hourly) and "2026-10-17" (daily and weekly, midnight UTC). Epoch ms, or null.
 */
export function parseFrameTime(key) {
  if (typeof key !== 'string') return null
  let m
  if ((m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})(\d{2})Z$/.exec(key))) {
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  }
  if ((m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})Z$/.exec(key))) {
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4])
  }
  if ((m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key))) {
    return Date.UTC(+m[1], +m[2] - 1, +m[3])
  }
  return null
}

/**
 * Seconds in an ISO 8601 duration of the shapes the index uses: PT10M, PT1H, PT6H, P1D,
 * P7D. Years and months are not used, so they are rejected.
 */
export function parseDuration(value) {
  if (typeof value !== 'string') return null
  const m = /^P(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i.exec(value)
  if (!m) return null
  const [, w, d, h, min, s] = m.map(Number)
  const total = (w || 0) * 7 * DAY + (d || 0) * DAY + (h || 0) * HOUR + (min || 0) * 60 + (s || 0)
  return total > 0 ? total : null
}

function parseRun(run) {
  if (!run) return null
  const t = Date.parse(run)
  return isNaN(t) ? null : t
}

// ---- Layers -------------------------------------------------------------------------

/**
 * A time layer per the services catalog: Layer/Get gives it a `timeStep` (null for every
 * other layer). iOS also probes pmtiles layers without one; web does not, because most
 * pmtiles layers have a `default` segment too and each probe would be a logged 404.
 */
export function isTimeLayer(layer) {
  return !!(layer && layer.timeStep && typeof layer.cloudfrontUrl === 'string' && layer.cloudfrontUrl.includes('/default/'))
}

/** `https://pmtiles.basemap.com/tiles/<ServerLayerName>/`, from the layer's CloudfrontURL. */
export function tilesBase(cloudfrontUrl) {
  if (typeof cloudfrontUrl !== 'string') return null
  const at = cloudfrontUrl.indexOf('/tiles/')
  if (at === -1) return null
  const slash = cloudfrontUrl.indexOf('/', at + '/tiles/'.length)
  if (slash === -1) return null
  return cloudfrontUrl.slice(0, slash + 1)
}

export function indexUrl(cloudfrontUrl) {
  const base = tilesBase(cloudfrontUrl)
  return base ? `${base}index.json` : null
}

/**
 * `?r=` value: the frame's `rev` (epoch seconds its archive was written) when the index
 * gives one, so a frame re-rendered under the same run gets new URLs and the browser's
 * day-long tile cache lets go of the old render. Without `rev`, the run as epoch seconds,
 * or "obs": a forecast hour that has passed is replaced by its observation at the same
 * path, so the run is part of every cache key (CloudFront keys these layers on `r` only).
 * The run fallback is iOS and Android's value, so those frames share CloudFront entries.
 */
export function frameRevision(frame) {
  if (frame.rev != null) return String(frame.rev)
  if (!frame.isForecast) return 'obs'
  if (frame.runTime != null) return String(Math.floor(frame.runTime / 1000))
  return frame.run || 'fc'
}

/**
 * `?r=` for a layer's live `default` tiles: its default frame's rev, or its forecast run.
 * Null for an observed default without rev, whose live URL stays as it always was.
 */
export function liveRevision(track) {
  const frame = track && track.defaultFrame
  if (!frame) return null
  if (frame.rev != null || frame.isForecast) return frameRevision(frame)
  return null
}

/**
 * A time layer that is on, with its frames parsed and sorted, or null when the index is
 * unusable. `layer` is a layersDictionary entry.
 */
export function makeTrack(layer, index) {
  const step = parseDuration(index && index.step)
  if (!step || !Array.isArray(index.times)) return null
  const info = index.frames || {}
  const frames = index.times
    .map(key => {
      const time = parseFrameTime(key)
      if (time == null) return null
      const f = info[key] || {}
      return {
        key,
        time,
        isForecast: f.kind === 'forecast',
        run: f.run || null,
        runTime: parseRun(f.run),
        rev: Number.isInteger(f.rev) ? f.rev : null,
        model: f.model || null,
        hasUV: f.uv === true,
        hasPX: f.px === true,
        hasPX10: f.px10 === true
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time)
  if (frames.length === 0) return null
  // A `default` missing from `times` falls back to the newest observed frame, then the first.
  const defaultKey = frames.some(f => f.key === index.default)
    ? index.default
    : ([...frames].reverse().find(f => !f.isForecast) || frames[0]).key
  return {
    layerId: layer.id,
    name: layer.name,
    drawOrder: layer.drawOrder || 0,
    urlTemplate: layer.cloudfrontUrl,
    tilesBase: tilesBase(layer.cloudfrontUrl),
    step,
    frames,
    defaultKey,
    defaultFrame: frames.find(f => f.key === defaultKey),
    hasWindField: frames.some(f => f.hasUV),
    hasPrecipField: frames.some(f => f.hasPX)
  }
}

/** Tile template of one frame, literal {z}/{x}/{y} (TMS y), keyed by `?r=`. */
export function frameTemplate(track, frame) {
  const r = encodeURIComponent(frameRevision(frame))
  return `${track.urlTemplate.replace('/default/', `/${frame.key}/`)}?r=${r}`
}

/** The 0.25° wind vector field of a frame (`<time>.uv25`), or null when it has none. */
export function windFieldUrl(track, frame) {
  if (!frame || !frame.hasUV || !track.tilesBase) return null
  return `${track.tilesBase}${frame.key}.uv25?r=${encodeURIComponent(frameRevision(frame))}`
}

/**
 * The 0.1° radar field (`<time>.px10`, flagged `px10`): Matt declined it for now
 * (2026-10-19), so every layer reads the 0.25° `.px25`. Turning this on is the whole
 * change; a frame without px10 still reads px25.
 */
export const PX10_ENABLED = false

/** The rain/snow field of a frame (`.px25`, or `.px10` when enabled), or null. */
export function precipFieldUrl(track, frame) {
  if (!frame || !frame.hasPX || !track.tilesBase) return null
  const ext = PX10_ENABLED && frame.hasPX10 ? 'px10' : 'px25'
  return `${track.tilesBase}${frame.key}.${ext}?r=${encodeURIComponent(frameRevision(frame))}`
}

/**
 * Each layer's rain intensity scale (Matt's rain retune, 2026-10-19): the shared module
 * turns rate x hours into the period's amount and places it on a log scale from lo to hi
 * inches. Snowfall 24 hr is snow only, so it has none.
 */
const RAIN_SCALES = {
  'USA:usa_precip_24h': { hours: 24, lo: 0.1, hi: 2 },
  'USA:usa_precip_1h': { hours: 1, lo: 0.01, hi: 1 },
  'USA:usa_radar': { hours: 1, lo: 0.01, hi: 1 }
}

export function serverLayerName(track) {
  const m = track && track.tilesBase && /\/tiles\/([^/]+)\/$/.exec(track.tilesBase)
  return m ? m[1] : null
}

/** {hours, lo, hi} for a precipitation track, or null (snow only, or not a known layer). */
export function rainScaleOf(track) {
  return RAIN_SCALES[serverLayerName(track)] || null
}

/** Where precipitation drift reads its wind: USA:usa_wind on the same tile host. */
export const DRIFT_WIND_LAYER = 'USA:usa_wind'

export function driftWindBase(track) {
  if (!track.tilesBase) return null
  return track.tilesBase.replace(/[^/]+\/$/, `${DRIFT_WIND_LAYER}/`)
}

/**
 * The wind field nearest in time to `time` from a usa_wind index.json, for precipitation
 * drift. Wind is hourly and radar every 10 minutes, so radar takes the nearest hour.
 */
export function nearestWindFieldUrl(windIndex, base, time) {
  if (!windIndex || !base) return null
  const track = makeTrack({ id: 'drift-wind', cloudfrontUrl: `${base}x/default/{z}/{x}/{y}.png` }, windIndex)
  if (!track) return null
  let best = null
  track.frames.forEach(f => {
    if (f.hasUV && (!best || Math.abs(f.time - time) < Math.abs(best.time - time))) best = f
  })
  return best ? windFieldUrl(track, best) : null
}

// ---- Clock --------------------------------------------------------------------------

/**
 * The range play loops for one layer: its whole track, radar included (Matt, agreed with
 * iOS and Android: radar runs from -3 h observed through its forecast to about +16 h).
 * Archive layers arrive already cut to their history range.
 */
export function playWindow(track) {
  return [track.frames[0].time, track.frames[track.frames.length - 1].time]
}

/** Sub-half-hour frames: radar (PT10M). */
export function isRadarStep(step) {
  return step < HOUR / 2
}

/** Thins `indices` to at most `max`, evenly, always keeping both ends and `keeping`. */
export function sampleIndices(indices, keeping, max) {
  if (indices.length <= max || max < 2) return indices
  const picked = new Set()
  for (let i = 0; i < max; i++) {
    picked.add(indices[Math.round(i * (indices.length - 1) / (max - 1))])
  }
  if (!picked.has(keeping)) {
    // Swap "now" in for whichever sampled stop is nearest to it, but never an end.
    const ends = [indices[0], indices[indices.length - 1]]
    let nearest = null
    picked.forEach(p => {
      if (ends.includes(p)) return
      if (nearest === null || Math.abs(p - keeping) < Math.abs(nearest - keeping)) nearest = p
    })
    if (nearest !== null) picked.delete(nearest)
    picked.add(keeping)
  }
  return [...picked].sort((a, b) => a - b)
}

/**
 * Every active time layer on ONE clock (agreed between iOS and Android): the slider stops
 * are the union of the layers' frame times, and at each stop a layer shows its latest
 * frame at or before the clock. On "now" every layer shows its live `default` tiles,
 * exactly as it does with the time bar untouched. Null when no track is usable.
 */
export function makeTimeline(tracks) {
  tracks = tracks.filter(t => t && t.defaultFrame)
  if (tracks.length === 0) return null
  const stops = [...new Set(tracks.flatMap(t => t.frames.map(f => f.time)))].sort((a, b) => a - b)
  // "Now" is the newest OBSERVED default. A forecast-only layer's default is a future
  // window; only when no layer has an observation does the earliest default stand in.
  const observed = tracks.map(t => t.defaultFrame).filter(f => !f.isForecast).map(f => f.time)
  const now = observed.length ? Math.max(...observed) : Math.min(...tracks.map(t => t.defaultFrame.time))
  const nowIndex = stops.indexOf(now) === -1 ? stops.length - 1 : stops.indexOf(now)
  const finestStep = Math.min(...tracks.map(t => t.step))

  // Play loops the finest-step layer's own frames over its window; the others follow the
  // clock. Looping the union of every window sampled radar down to 2-hour jumps.
  const lead = tracks.reduce((a, b) => (b.step < a.step ? b : a))
  const [lo, hi] = playWindow(lead)
  const inWindow = new Set([nowIndex])
  lead.frames.forEach(f => {
    if (f.time >= lo && f.time <= hi) inWindow.add(stops.indexOf(f.time))
  })
  const loop = sampleIndices([...inWindow].sort((a, b) => a - b), nowIndex, MAX_LOOP_FRAMES)

  return { tracks, stops, nowIndex, finestStep, loop }
}

/** The frame `track` shows at stop `index`, or null for its live `default` tiles. */
export function frameAt(timeline, track, index) {
  if (index === timeline.nowIndex || index < 0 || index >= timeline.stops.length) return null
  const clock = timeline.stops[index]
  let frame = track.frames[0]
  for (const f of track.frames) {
    if (f.time <= clock) frame = f
    else break
  }
  return frame.key === track.defaultKey ? null : frame
}

/** The frame `track` draws at `index`, the live frame included, for labels. */
export function displayedFrameAt(timeline, track, index) {
  return frameAt(timeline, track, index) || track.defaultFrame
}

/**
 * True when `track` has no frame close to the clock, so it is showing an older one (a
 * weekly drought map under an hourly temperature clock).
 */
export function isStale(timeline, track, index) {
  const shown = displayedFrameAt(timeline, track, index)
  const clock = timeline.stops[index]
  if (shown == null || clock == null) return false
  return clock - shown.time >= track.step * 1500 || shown.time > clock
}

/** The layer whose drawn frame is nearest the clock, which the bar's label describes. */
export function labelTrackAt(timeline, index) {
  const clock = timeline.stops[index]
  if (clock == null) return null
  let best = null
  timeline.tracks.forEach(track => {
    const frame = displayedFrameAt(timeline, track, index)
    if (!best || Math.abs(frame.time - clock) < Math.abs(best.frame.time - clock)) best = { track, frame }
  })
  return best
}

/** The stop nearest `time`, for keeping the slider's place across an index refresh. */
export function nearestStop(timeline, time) {
  let best = 0
  timeline.stops.forEach((stop, i) => {
    if (Math.abs(stop - time) < Math.abs(timeline.stops[best] - time)) best = i
  })
  return best
}

/**
 * Every distinct (track, frame) the play loop needs, for prefetching, in playback order
 * from loop position `start`, so the frames shown first load first.
 */
export function loopFrames(timeline, start = 0) {
  const seen = new Set()
  const out = []
  const loop = timeline.loop
  const first = loop.length ? Math.max(0, Math.min(start, loop.length - 1)) : 0
  loop.slice(first).concat(loop.slice(0, first)).forEach(index => {
    timeline.tracks.forEach(track => {
      const frame = frameAt(timeline, track, index)
      if (!frame) return
      const id = `${track.layerId}/${frame.key}`
      if (!seen.has(id)) {
        seen.add(id)
        out.push({ track, frame })
      }
    })
  })
  return out
}

/**
 * The layer the particles follow: the upper of every layer with a wind (uv) or rain/snow
 * (px) field. One effect at a time, so precipitation on top hides the wind streaks and
 * wind on top hides the rain.
 */
export function particleTrackOf(timeline) {
  if (!timeline) return null
  return timeline.tracks
    .filter(t => t.hasWindField || t.hasPrecipField)
    .reduce((a, b) => (!a || b.drawOrder > a.drawOrder ? b : a), null)
}

/** 'precip' or 'wind' for a particle track. */
export function particleKindOf(track) {
  return track && track.hasPrecipField ? 'precip' : 'wind'
}

// ---- Labels -------------------------------------------------------------------------

const timeFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
// Daily and weekly frames are dates, not instants: UTC so "2026-10-17" never becomes Oct 16.
const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
const runFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric' })

export function frameLabel(time, step) {
  if (step >= 7 * DAY) return `Week of ${dateFormat.format(time)}`
  if (step >= DAY) return dateFormat.format(time)
  return timeFormat.format(time)
}

export function runLabel(runTime) {
  return runFormat.format(runTime)
}

/** "-1h 20m", "+6h", "-3d", relative to the wall clock, or null when close to now. */
export function relativeLabel(time, step, nowMs = Date.now()) {
  const seconds = (time - nowMs) / 1000
  const sign = seconds < 0 ? '-' : '+'
  const abs = Math.abs(seconds)
  if (step >= DAY) {
    const days = Math.round(abs / DAY)
    return days === 0 ? null : `${sign}${days}d`
  }
  const minutes = Math.round(abs / 60)
  if (minutes < 5) return null
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours >= 48) return `${sign}${Math.floor(hours / 24)}d`
  if (hours === 0) return `${sign}${rest}m`
  // Hourly and coarser frames sit on the hour; drop the minutes noise.
  if (step >= HOUR || rest === 0) return `${sign}${Math.round(abs / HOUR)}h`
  return `${sign}${hours}h ${rest}m`
}

// ---- Weather mode ---------------------------------------------------------------------

/**
 * The weather layer families the bar's layer row and header know, in the row's order,
 * with the chip's short name. Matched on the server layer name and the layer name, as on
 * iOS (TimeLayerKind) and Android.
 */
export const TIME_LAYER_KINDS = [
  { kind: 'radar', short: 'Radar', test: k => k.includes('radar') },
  { kind: 'temperature', short: 'Temp', test: k => k.includes('temp') },
  { kind: 'wind', short: 'Wind', test: k => k.includes('wind') },
  { kind: 'gusts', short: 'Gusts', test: k => k.includes('gust') },
  { kind: 'cloud', short: 'Clouds', test: k => k.includes('cloud') },
  { kind: 'precip1h', short: 'Precip 1h', test: k => k.includes('precip') && !k.includes('24') && !/chance|probab|_pop/.test(k) },
  { kind: 'precip24h', short: 'Precip 24h', test: k => k.includes('precip') && k.includes('24') && !/chance|probab|_pop/.test(k) },
  { kind: 'snowfall', short: 'Snowfall', test: k => k.includes('snowfall') },
  { kind: 'chanceOfPrecip', short: 'Chance', test: k => /chance|probab|_pop/.test(k) },
  { kind: 'snowDepth', short: 'Snow Depth', test: k => /snow_depth|snowdepth|snow depth/.test(k) },
  { kind: 'drought', short: 'Drought', test: k => k.includes('drought') },
  { kind: 'deerMovement', short: 'Deer', test: k => k.includes('deer') }
]
// iOS checks these in its own precedence (radar, gust, wind, temp, cloud, chance, snowfall,
// snow depth, drought, precip); the row order above is its enum order. Deer movement goes
// first: it is not weather, so no weather word should ever claim it.
const KIND_PRECEDENCE = ['deerMovement', 'radar', 'gusts', 'wind', 'temperature', 'cloud', 'chanceOfPrecip', 'snowfall', 'snowDepth', 'drought', 'precip24h', 'precip1h']

/** {kind, short, order} for a layersDictionary entry or a track. */
export function timeLayerKind(layer) {
  const server = layer && (layer.cloudfrontUrl || layer.urlTemplate)
  const name = serverLayerName({ tilesBase: tilesBase(server) }) || ''
  const key = `${name} ${(layer && layer.name) || ''}`.toLowerCase()
  const found = KIND_PRECEDENCE.map(k => TIME_LAYER_KINDS.find(d => d.kind === k)).find(d => d.test(key))
  if (!found) return { kind: 'other', short: null, order: TIME_LAYER_KINDS.length }
  return { kind: found.kind, short: found.short, order: TIME_LAYER_KINDS.indexOf(found) }
}

/** This track with only the frames from `time` on; the `default` frame always stays. */
export function keepFramesFrom(track, time) {
  return { ...track, frames: track.frames.filter(f => f.time >= time || f.key === track.defaultKey) }
}

/**
 * The history ranges an archive layer offers: 7D/30D/90D/1Y for daily layers (snow depth),
 * 3M/6M/1Y for weekly ones (drought). A range is offered only when the archive reaches
 * past the range before it, so every choice changes something. Null for every other layer.
 */
export function historyRanges(track) {
  if (!track || track.frames.some(f => f.isForecast) || track.step < DAY / 2) return null
  const all = track.step >= 6 * DAY
    ? [['3M', 91], ['6M', 182], ['1Y', 364]]
    : [['7D', 7], ['30D', 30], ['90D', 90], ['1Y', 365]]
  const preferred = track.step >= 6 * DAY ? '1Y' : '30D'
  const span = (track.frames[track.frames.length - 1].time - track.frames[0].time) / 1000
  const options = all
    .filter((_, i) => i === 0 || all[i - 1][1] * DAY < span)
    .map(([label, days]) => ({ label, seconds: days * DAY }))
  if (options.length < 2) return null
  return { options, defaultLabel: options.some(o => o.label === preferred) ? preferred : options[options.length - 1].label }
}

/** The track cut to its chosen history range (`label`, else the default), when it has one. */
export function rangedTrack(track, label) {
  const ranges = historyRanges(track)
  if (!ranges) return { track, range: null }
  const range = ranges.options.find(o => o.label === label) || ranges.options.find(o => o.label === ranges.defaultLabel)
  return { track: keepFramesFrom(track, track.defaultFrame.time - range.seconds * 1000), range: range.label }
}

const stampTimeFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
const stampDateFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

/** "Wed 2:00 PM", or "Wed, Oct 21" (UTC) for daily and weekly frames. */
export function stampLabel(time, step) {
  return step >= DAY ? stampDateFormat.format(time) : stampTimeFormat.format(time)
}

/** "\u221240 min", "+5 h", "+3 d": how far `time` is from `reference`. */
export function offsetLabel(time, reference) {
  const seconds = (time - reference) / 1000
  const sign = seconds < 0 ? '\u2212' : '+'
  const hours = Math.abs(seconds) / HOUR
  if (hours < 1) return `${sign}${Math.max(1, Math.round(Math.abs(seconds) / 60))} min`
  if (hours < 48) return `${sign}${Math.round(hours)} h`
  return `${sign}${Math.round(hours / 24)} d`
}

/** "10 min", "1 h", "3 h", "daily", "weekly": a band's cadence, and the step readout. */
export function stepText(step) {
  if (step < HOUR) return `${Math.max(1, Math.round(step / 60))} min`
  if (step < DAY) return `${Math.round(step / HOUR)} h`
  if (step < 7 * DAY) return 'daily'
  return 'weekly'
}

export const GAP_WIDTH = 14
const MIN_DAY_LABEL_GAP = 34

/**
 * Where the bar's track draws each frame, its cadence bands and its day labels (iOS
 * TimeBarTrackLayout). Every frame gets the same width, so a 10-minute radar frame and a
 * 6-hour precipitation frame take the same drag distance. Bands show where the cadence
 * changes (1 h, then 3 h, then 6 h) and where observed (white) turns into forecast
 * (green). A jump far coarser than the frames either side of it (precipitation 24 h's
 * forecast starts a day out) is drawn as a short hatched gap rather than as frames.
 *
 * `times` epoch ms ascending, `forecast` a flag per time. `nowIndex` null means the layer
 * has no observed half: NOW then sits at the left edge. `utc` labels days in UTC, for
 * daily and weekly frames, which are dates rather than instants.
 */
export function trackLayout({ times, forecast, nowIndex, step, width, utc, nowMs = Date.now() }) {
  const count = times.length
  const empty = { width, segments: [], xs: [], steps: [], nowX: 0, days: [], showsFrameTicks: false }
  if (count === 0 || forecast.length !== count || !(width > 0)) return empty
  const deltas = times.slice(1).map((t, i) => (t - times[i]) / 1000)
  // A gap sits between frames i and i+1 when that jump is more than twice the cadence on
  // both sides of it.
  const gapAfter = deltas.map((d, i) => {
    const around = Math.max(i > 0 ? deltas[i - 1] : 0, i + 1 < deltas.length ? deltas[i + 1] : 0)
    return around > 0 && d > 2 * around + 1
  })
  // A frame's cadence is the time since the frame before it, so the frame where the
  // cadence changes closes the finer band (temperature's +48 h ends the hourly band).
  const steps = times.map((_, i) => {
    if (i > 0 && !gapAfter[i - 1]) return deltas[i - 1]
    if (i < deltas.length && !gapAfter[i]) return deltas[i]
    return step
  })
  const gaps = gapAfter.filter(Boolean).length
  const perFrame = Math.max(0, width - gaps * GAP_WIDTH) / count
  const segments = []
  const xs = []
  let x = 0
  let runStart = 0
  for (let i = 0; i < count; i++) {
    xs.push(x + (i - runStart + 0.5) * perFrame)
    const isLast = i === count - 1
    const gap = !isLast && gapAfter[i]
    const changes = !isLast && (forecast[i + 1] !== forecast[i] || Math.abs(steps[i + 1] - steps[i]) > 1)
    if (isLast || gap || changes) {
      const frames = i - runStart + 1
      segments.push({ x, width: frames * perFrame, kind: forecast[runStart] ? 'forecast' : 'observed', step: steps[runStart] })
      x += frames * perFrame
      runStart = i + 1
    }
    if (gap) {
      segments.push({ x, width: GAP_WIDTH, kind: 'gap', step: 0 })
      x += GAP_WIDTH
    }
  }
  const nowX = nowIndex != null && xs[nowIndex] != null ? xs[nowIndex] : times[0] > nowMs ? 0 : width

  // Day labels land on the first frame of each day (or month, over long archives).
  const span = (times[count - 1] - times[0]) / 1000
  const timeZone = utc ? 'UTC' : undefined
  const format = new Intl.DateTimeFormat(undefined, span <= 14 * DAY
    ? { weekday: 'short', timeZone } : span <= 100 * DAY ? { month: 'short', day: 'numeric', timeZone } : { month: 'short', timeZone })
  const keyFormat = new Intl.DateTimeFormat('en-US', span > 100 * DAY
    ? { year: 'numeric', month: 'numeric', timeZone } : { year: 'numeric', month: 'numeric', day: 'numeric', timeZone })
  const days = []
  let lastKey = null
  let lastX = -Infinity
  times.forEach((t, i) => {
    const key = keyFormat.format(t)
    if (key === lastKey) return
    lastKey = key
    const fx = xs[i]
    if (fx - lastX >= MIN_DAY_LABEL_GAP && fx > 10 && fx < width - 14) {
      days.push({ x: fx, text: format.format(t) })
      lastX = fx
    }
  })
  return { width, segments, xs, steps, nowX, days, showsFrameTicks: perFrame >= 5 }
}

/** The frame nearest `x` on a track layout, for scrubbing. */
export function indexNearest(layout, x) {
  let best = 0
  layout.xs.forEach((fx, i) => {
    if (Math.abs(fx - x) < Math.abs(layout.xs[best] - x)) best = i
  })
  return best
}
